import type { FileConfig } from '../types/FileConfig';

/**
 * Configuration objects for MicroPulse tool file types
 * Each config defines how to parse a specific MicroPulse CSV file format
 */
export const MICROPULSE_CONFIGS: Record<string, FileConfig> = {
  MICROPULSE_FLOW: { 
    timeColumns: ['Time'], 
    expectedColumns: ['Time', 'Lateral StDev', 'Axial StDev', 'Internal Flow State', 'External Flow State'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_FLOW',
    dataType: 'timeseries',
    timestampConfig: {
      timezone: 'CST'  // Central Standard Time (UTC-6) - typical for drilling operations
    }
  },
  MICROPULSE_PULSE: { 
    timeColumns: ['Time', 'Timestamp'], 
    expectedColumns: [
      // Old format columns
      'Time', 'Pulse',
      // New 3.4.6 format columns (renamed "Pulse" to "Pulse State", no "Time" column)
      'Pulse State', 'Timestamp'
    ],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_PULSE',
    dataType: 'timeseries',
    timestampConfig: {
      timezone: 'none',  // No timezone conversion for pulse files
      supportsSpaceSeparatedMs: true  // Handle "HH:MM:SS 5" -> "HH:MM:SS.500" format
    }
  },
  MICROPULSE_ROTGAMMA: { 
    timeColumns: ['RTC_TIME'], 
    expectedColumns: ['RTC_TIME', 'ROT_GAMMA_SECT_1', 'ROT_GAMMA_SECT_2', 'ROT_GAMMA_SECT_16', 'RPM'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_ROTGAMMA',
    dataType: 'timeseries',
    timestampConfig: {
      timezone: 'CST'  // Central Standard Time (UTC-6) - typical for drilling operations
    },
    // Column mappings to clean up display names
    columnMappings: {
      'Clock Ticks (ms)': 'Clock Ticks',
      'Rotational Gamma CPS (cps)': 'Rotational Gamma CPS',
      'Tool Temperature (°C)': 'Tool Temperature',
      'Gravity Tool-Face (degrees)': 'Gravity Tool-Face',
      'Magnetic Tool-Face (degrees)': 'Magnetic Tool-Face'
    },
    autoClean: true // Enable automatic cleaning as fallback
  },
  MICROPULSE_SURVEY: { 
    timeColumns: ['Time', 'Timestamp'], 
    expectedColumns: ['Inclination', 'Azimuth'], // Core survey columns - flexible detection in parser
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_SURVEY',
    dataType: 'survey',
    // Column mappings for common survey columns with units
    columnMappings: {
      'Inclination (Degrees)': 'Inclination',
      'Azimuth (Degrees)': 'Azimuth',
      'Gravity Tool-Face (degrees)': 'Gravity Tool-Face',
      'Magnetic Tool-Face (degrees)': 'Magnetic Tool-Face',
      'Temperature (Celsius)': 'Temperature',
      'Dip Angle (degrees)': 'Dip Angle'
    },
    autoClean: true
  },
  MICROPULSE_VIBE: { 
    timeColumns: ['RTC_TIME', 'Timestamp'], 
    expectedColumns: ['RTC_TIME', 'XH_ACCEL', 'ZH_ACCEL', 'XL_ACCEL', 'ZL_ACCEL', 'FLOW'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_VIBE',
    dataType: 'timeseries'
  },
  MICROPULSE_DEBUG: {
    timeColumns: ['RTC_TIME'],
    expectedColumns: ['CLOCK_TICKS', 'RTC_TIME', 'DBG_FLOAT1', 'DBG_FLOAT2', 'INCLINATION', 'AZIMUTH', 'RPM'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_DEBUG',
    dataType: 'timeseries'
  },
  MICROPULSE_GAMMA: {
    timeColumns: ['RTC_TIME', 'Timestamp', 'Clock Ticks (ms)'],
    expectedColumns: [
      // Old format columns
      'RTC_TIME', 'GAMMA1',
      // New 3.4.6 format columns - timing
      'Clock Ticks (ms)', 'Timestamp',
      // New 3.4.6 format columns - gamma sensors
      'Gamma1 (API/CPS)', 'Gamma2 (API/CPS)', 'Gamma3 (API/CPS)',
      // New 3.4.6 format columns - rotation gamma sectors (16 sectors)
      'Rotation Gamma Sector 1 (CPS/sector)', 'Rotation Gamma Sector 2 (CPS/sector)', 'Rotation Gamma Sector 3 (CPS/sector)', 'Rotation Gamma Sector 4 (CPS/sector)',
      'Rotation Gamma Sector 5 (CPS/sector)', 'Rotation Gamma Sector 6 (CPS/sector)', 'Rotation Gamma Sector 7 (CPS/sector)', 'Rotation Gamma Sector 8 (CPS/sector)',
      'Rotation Gamma Sector 9 (CPS/sector)', 'Rotation Gamma Sector 10 (CPS/sector)', 'Rotation Gamma Sector 11 (CPS/sector)', 'Rotation Gamma Sector 12 (CPS/sector)',
      'Rotation Gamma Sector 13 (CPS/sector)', 'Rotation Gamma Sector 14 (CPS/sector)', 'Rotation Gamma Sector 15 (CPS/sector)', 'Rotation Gamma Sector 16 (CPS/sector)',
      // New 3.4.6 format columns - azimuthal gamma
      'AziGamma Survey Toolface Offset (degrees)'
    ],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_GAMMA',
    dataType: 'timeseries',
    timestampConfig: {
      timezone: 'MDT'  // Mountain Daylight Time (UTC-6) conversion for GAMMA files
    }
  },
  MICROPULSE_TEMP: {
    timeColumns: ['LOGTAG_RTC_TIME'],
    expectedColumns: ['LOGTAG_RTC_TIME', 'TEMPERATURE', 'TEMPERATURE_ADC'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_TEMP',
    dataType: 'timeseries'
  },
  MICROPULSE_VOLTAGES: {
    timeColumns: ['RTC_TIME', 'Timestamp', 'Clock Ticks (ms)'],
    expectedColumns: [
      // Old format columns
      'RTC_TIME', 'BAT1V', 'BAT2V', 'BAT3V', 'BUS', '5V', '3V3', '1V8', 'SYS_CURRENT',
      // New 3.4.6 format columns - timing
      'Clock Ticks (ms)', 'Timestamp',
      // New 3.4.6 format columns - battery statistics
      'Battery 1 Mean (V)', 'Battery 1 Min (V)', 'Battery 1 Max (V)', 'Battery 1 Standard Deviation (V)',
      'Battery 2 Mean (V)', 'Battery 2 Min (V)', 'Battery 2 Max (V)', 'Battery 2 Standard Deviation (V)',
      'Battery Bus Mean (V)', 'Battery Bus Min (V)', 'Battery Bus Max (V)', 'Battery Bus Standard Deviation (V)',
      // New 3.4.6 format columns - rail statistics  
      '1.8V Rail Mean (V)', '1.8V Rail Min (V)', '1.8V Rail Max (V)', '1.8V Rail Standard Deviation (V)',
      '3.3V Rail Mean (V)', '3.3V Rail Min (V)', '3.3V Rail Max (V)', '3.3V Rail Standard Deviation (V)',
      '5.0V Rail Mean (V)', '5.0V Rail Min (V)', '5.0V Rail Max (V)', '5.0V Rail Standard Deviation (V)',
      // New 3.4.6 format columns - current statistics
      'System Current Mean (V)', 'System Current Min (V)', 'System Current Max (V)', 'System Current Standard Deviation (V)',
      // New 3.4.6 format columns - temperatures
      'CPU Temperature (celcius)', 'Accelerometer Temperature (celcius)', 'Magnetometer Temperature (celcius)', 'ADC Temperature (mV)',
      // New 3.4.6 format columns - hardware timing
      'Hardware RTC Time'
    ],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_VOLTAGES',
    dataType: 'timeseries'
  },
  MICROPULSE_ROUTE: {
    timeColumns: ['RTC_TIME'],
    expectedColumns: ['RTC_TIME', 'CINC', 'CAZM'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_ROUTE',
    dataType: 'timeseries'
  },
  MICROPULSE_SYSTEM: {
    timeColumns: ['RTC_TIME', 'Timestamp', 'Clock Ticks (ms)'], // Support old format (RTC_TIME) and new 3.4.6 format (Timestamp, Clock Ticks)
    expectedColumns: ['CLOCK_TICKS', 'RTC_TIME', 'STRING_MSG', 'HARDWARE_VERSION', 'FIRMWARE_VERSION', 'Clock Ticks (ms)', 'System Event', 'Other Info'], // Support both old and new 3.4.6 formats
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_SYSTEM',
    dataType: 'timeseries'
  },
  MICROPULSE_TELEM: {
    timeColumns: ['LOGTAG_RTC_TIME', 'Timestamp'], // Support both old and new formats
    expectedColumns: [
      // Old format columns
      'TELEM_VARIABLE', 'VALUE', 'BITSTREAM', 'TELEMETERED_VALUE', 'LOGTAG_RTC_TIME',
      // New 3.4.6 format columns (different case sensitivity, no Clock Ticks)
      'Telem Variable', 'Value', 'Bitstream', 'Telemetered Value', 'Timestamp'
    ],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_TELEM',
    dataType: 'timeseries',
    timestampConfig: {
      timezone: 'CST',  // Central Standard Time (UTC-6) - typical for drilling operations
      inheritTimestamps: true  // Allow empty timestamps to inherit from previous valid timestamp
    }
  },
  MICROPULSE_CONFIG: {
    timeColumns: ['Timestamp'],
    expectedColumns: ['Clock Ticks (ms)', 'Timestamp', 'Config Item', 'Value', 'Index'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_CONFIG',
    dataType: 'timeseries'
  },
  MICROPULSE_CONTINUOUS_MEAS: {
    timeColumns: ['Timestamp'],
    expectedColumns: ['Clock Ticks (ms)', 'Timestamp', 'Continuous Inclination (degrees)', 'Debug Z-Inclination (degrees)', 'Continuous Azimuth (degrees)', 'Average RPM (RPM)', 'Minimum RPM (RPM)', 'Maximum RPM (RPM)', 'Lateral Standard Deviation (g RMS)', 'Axial Standard Deviation (g RMS)', 'Internal Flow State', 'External/GPIO Flow State'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_CONTINUOUS_MEAS',
    dataType: 'timeseries'
  },
  MICROPULSE_SHOCK: {
    timeColumns: ['RTC_TIME'],
    expectedColumns: ['CLOCK_TICKS', 'RTC_TIME', 'X_SHOCK', 'Y_SHOCK', 'Z_SHOCK', 'LSMX', 'ASMX'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_SHOCK',
    dataType: 'timeseries'
  },
  MICROPULSE_SHOCK_VIBE: {
    timeColumns: ['Timestamp'],
    expectedColumns: ['Clock Ticks (ms)', 'Timestamp', 'Vibe X (g RMS)', 'Vibe Y (g RMS)', 'Vibe Z (g RMS)', 'Max Shock X (g)', 'Max Shock Y (g)', 'Max Shock Z (g)', 'Lateral Shock Counts', 'Axial Shock Counts'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_SHOCK_VIBE',
    dataType: 'timeseries'
  },
  MICROPULSE_RSS: {
    timeColumns: ['Timestamp'],
    expectedColumns: ['Clock Ticks (ms)', 'Timestamp', 'INCL (degrees)', 'AZIM (degrees)', 'RTSTAT', 'TFDS (degrees)', 'PRDS (degrees)', 'TFHI (degrees)', 'SHKRSK', 'TF (degrees)', 'PROPEFF (%)', 'IH_TRGT (degrees)', 'UTRPM (rpm)', 'LTRPM (rpm)', 'POSSUM (%)', 'AZI_TRGT (degrees)', 'STEER', 'PSKSNR (dB)', 'GRUP (CPS)', 'GRLF (CPS)', 'GRRT (CPS)', 'GRDN (CPS)', 'RTSTAT2', 'RTSTAT3', 'RTSTAT4', 'PD3B3', 'DLNK', 'PDSTAT', 'PDTEMP (degrees)', 'CCRPM (RPM)', 'BARKER', 'RXERR (%)', 'RXERRCT', 'RXCT', 'LATSHKPEAK (g)', 'LATVIB (g RMS)', 'AXISHKPEAK (g)', 'AXIVIB (g RMS)', 'STKSLP'],
    toolType: 'MicroPulse',
    fileType: 'EM_MICROPULSE_RSS',
    dataType: 'timeseries'
  }
};